<template>
	<aside
		class="relative hidden shrink-0 flex-col overflow-hidden bg-gradient-to-b from-navy-800 via-navy-800 to-navy-900 transition-all duration-200 lg:flex"
		:class="collapsed ? 'w-[76px]' : 'w-[220px]'"
	>
		<!-- Brand -->
		<div class="flex items-center gap-3 px-5 py-5">
			<span class="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white">
				<Plane class="h-5 w-5 -rotate-45" />
			</span>
			<div v-if="!collapsed" class="leading-tight">
				<p class="text-[15px] font-bold text-white">Trip Planner</p>
				<p class="text-[10px] tracking-[0.16em] text-brand-200/80">PLAN · BOOK · TRAVEL</p>
			</div>
		</div>

		<!-- Navigation -->
		<nav class="scroll-slim relative z-10 flex-1 space-y-0.5 overflow-y-auto px-3 pb-6">
			<template v-for="item in navigation" :key="item.label">
				<router-link
					v-if="!item.children"
					:to="item.to"
					class="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition"
					:class="isActive(item) ? 'bg-brand-600 text-white shadow-sm' : 'text-brand-100/80 hover:bg-white/10 hover:text-white'"
				>
					<component :is="item.icon" class="h-[18px] w-[18px] shrink-0" />
					<span v-if="!collapsed" class="truncate">{{ item.label }}</span>
				</router-link>

				<div v-else>
					<button
						type="button"
						class="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition"
						:class="isActive(item) ? 'bg-brand-600 text-white shadow-sm' : 'text-brand-100/80 hover:bg-white/10 hover:text-white'"
						@click="toggle(item.label)"
					>
						<component :is="item.icon" class="h-[18px] w-[18px] shrink-0" />
						<span v-if="!collapsed" class="flex-1 truncate text-left">{{ item.label }}</span>
						<ChevronRight
							v-if="!collapsed"
							class="h-4 w-4 transition-transform"
							:class="{ 'rotate-90': isOpen(item) }"
						/>
					</button>

					<div v-if="!collapsed && isOpen(item)" class="mb-1 mt-0.5 space-y-0.5">
						<router-link
							v-for="child in item.children"
							:key="child.label"
							:to="child.to"
							class="flex items-center gap-2.5 rounded-lg py-2 pl-10 pr-3 text-[13px] transition"
							:class="
								$route.path === child.to
									? 'bg-white/15 font-medium text-white'
									: 'text-brand-100/70 hover:bg-white/10 hover:text-white'
							"
						>
							<component :is="child.icon" class="h-[15px] w-[15px] shrink-0" />
							<span class="truncate">{{ child.label }}</span>
						</router-link>
					</div>
				</div>
			</template>
		</nav>

		<!-- Tagline and watermark, as in the mockups -->
		<div v-if="!collapsed" class="relative z-10 px-6 pb-6">
			<p class="text-[13px] italic leading-5 text-brand-100/70">
				Better Journeys<br />Bigger Dreams
			</p>
		</div>

		<Plane
			class="pointer-events-none absolute -bottom-6 -left-8 h-44 w-44 -rotate-[25deg] text-white/[0.05]"
			aria-hidden="true"
		/>
		<div
			class="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/20 to-transparent"
			aria-hidden="true"
		/>
	</aside>
</template>

<script setup>
import {
	BarChart3,
	Briefcase,
	CalendarDays,
	ChevronRight,
	ClipboardCheck,
	CreditCard,
	FileText,
	LayoutDashboard,
	ListTree,
	MapPinned,
	MessageSquareQuote,
	Package,
	Plane,
	Plus,
	Receipt,
	Settings,
	Tags,
	Truck,
	Users,
	Wallet,
} from "lucide-vue-next";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import { getMenuHref, loadMenus, menuState } from "../utils/menuStore";

defineProps({
	collapsed: { type: Boolean, default: false },
});

const route = useRoute();

const icons = {
	BarChart3,
	Briefcase,
	CalendarDays,
	ClipboardCheck,
	CreditCard,
	Database: ListTree,
	FileText,
	LayoutDashboard,
	ListTree,
	MapPinned,
	MessageSquareQuote,
	Package,
	Plane,
	Plus,
	Receipt,
	Settings,
	Tags,
	Truck,
	Users,
	Wallet,
};

const fallback = [
	{ label: "Dashboard", to: "/", icon: LayoutDashboard },
	{
		label: "Tours / Packages",
		to: "/tours",
		icon: Plane,
		match: ["/tours", "/tour-categories", "/tour-enquiry"],
		children: [
			{ label: "Tour Packages", to: "/tours", icon: Package },
			{ label: "Add Tour", to: "/tours/new", icon: Plus },
			{ label: "Tour Categories", to: "/tour-categories", icon: Tags },
			{ label: "Tour Enquiry", to: "/tour-enquiry", icon: MessageSquareQuote },
		],
	},
	{ label: "Itinerary", to: "/itinerary", icon: CalendarDays },
	{ label: "Customers", to: "/customers", icon: Users },
	{ label: "Suppliers", to: "/suppliers", icon: Truck },
	{ label: "Activities", to: "/services", icon: Briefcase, match: ["/services"] },
	{ label: "Quotations", to: "/quotations", icon: FileText },
	{ label: "Bookings", to: "/bookings", icon: MapPinned },
	{ label: "Supplier Requests", to: "/operations", icon: ClipboardCheck },
	{ label: "Payments", to: "/payments", icon: CreditCard },
	{ label: "Invoices", to: "/invoices", icon: Receipt },
	{ label: "Reports", to: "/reports", icon: BarChart3 },
	{ label: "Settings", to: "/settings", icon: Settings },
];

const navigation = computed(() => {
	const items = !menuState.fromApi || !menuState.menus.length ? fallback : menuState.menus.map(toNavItem);
	return items.map(asActivityLink);
});

function asActivityLink(item) {
	const paths = [item.to, ...(item.match || []), ...(item.children || []).map((child) => child.to)];
	const isServices =
		item.label === "Services" ||
		paths.some((path) => path === "/services" || String(path || "").startsWith("/services/"));
	if (!isServices) return item;
	return { label: "Activities", to: "/services", icon: item.icon || Briefcase, match: ["/services"] };
}

function toNavItem(item) {
	const children = (item.children || []).map(toNavItem);
	const to = children.length ? children[0].to : getMenuHref(item);
	return {
		label: item.name,
		to,
		icon: icons[item.icon] || FileText,
		match: children.length ? children.map((child) => child.to) : undefined,
		children: children.length ? children : undefined,
	};
}

onMounted(() => {
	loadMenus();
});

const openGroups = ref([]);

function matches(item, path) {
	const prefixes = item.match?.length ? item.match : [item.to];
	return prefixes.some((prefix) => {
		if (!prefix) return false;
		if (prefix === "/") return path === "/";
		return path === prefix || path.startsWith(`${prefix}/`);
	});
}

function isActive(item) {
	return matches(item, route.path);
}

function isOpen(item) {
	return openGroups.value.includes(item.label) || isActive(item);
}

function toggle(label) {
	const index = openGroups.value.indexOf(label);
	if (index === -1) openGroups.value.push(label);
	else openGroups.value.splice(index, 1);
}
</script>
